import React, { useState, useRef } from 'react';
import { Control, Controller, FieldError } from 'react-hook-form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Eye, EyeOff, ChevronDown } from 'lucide-react';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { cn } from '@/utils';

interface CustomInputProps {
  name: string;
  control: Control<any>;
  label?: string;
  type?: string;
  placeholder?: string;
  labelClassName?: string;
  inputClassName?: string;
  options?: { label: string; value: string | number }[];
  disabled?: boolean;
  showPreviewButton?: boolean;
  previewNewUrl?: string;
  maxLength?: number;
  onFileSelect?: (e: any) => void;
  [key: string]: any;
}

const CustomInput: React.FC<CustomInputProps> = ({
  name,
  control,
  label,
  type = 'text',
  placeholder,
  labelClassName = '',
  inputClassName = 'text-black',
  disabled = false,
  options = [],
  showPreviewButton = false,
  previewNewUrl,
  maxLength,
  onFileSelect,
  ...props
}) => {
  const [showPassword, setShowPassword] = useState(false);
  const [openSelect, setOpenSelect] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const isPassword = type === 'password';
  const isSelect = type === 'select';
  const isFile = type === 'file';
  const isPhone = type === 'phone';
  const isTextarea = type === 'textarea';

  return (
    <div className="space-y-1.5">
      {label && (
        <label htmlFor={name} className={cn('text-sm text-foreground', labelClassName)}>
          {label}
        </label>
      )}

      <Controller
        name={name}
        control={control}
        render={({ field, fieldState }) => {
          const error: FieldError | undefined = fieldState.error;

          /** PHONE INPUT */
          if (isPhone) {
            return (
              <>
                <PhoneInput
                  {...field}
                  country="bd"
                  value={field.value}
                  onChange={(v) => field.onChange(v.startsWith('+') ? v : '+' + v)}
                  inputClass={`!w-full !h-[38px] ${
                    error ? 'border-destructive' : ''
                  } ${inputClassName}`}
                  containerClass="w-full text-black"
                  placeholder={placeholder}
                  enableSearch
                />
                {error && <p className="text-xs text-destructive">{error.message}</p>}
              </>
            );
          }

          /** SELECT INPUT */
          if (isSelect) {
            const selected = options.find((o) => o.value === field.value);

            return (
              <div className="relative">
                <button
                  type="button"
                  disabled={disabled}
                  onClick={() => !disabled && setOpenSelect(!openSelect)}
                  className={cn(
                    'border rounded-md px-3 py-2 flex justify-between items-center h-[38px] w-full bg-white text-black',
                    error ? 'border-destructive' : 'border-gray-300',
                    inputClassName,
                    disabled && 'cursor-not-allowed opacity-60'
                  )}
                >
                  <span>{selected ? selected.label : placeholder}</span>
                  <ChevronDown className="h-4 w-4" />
                </button>

                {openSelect && !disabled && (
                  <div className="absolute mt-1 w-full bg-white text-black  border-green rounded-md shadow-md z-10 max-h-40 overflow-auto">
                    {options.map((opt, idx) => (
                      <div
                        key={idx}
                        className="px-3 py-2  cursor-pointer border-b"
                        onClick={() => {
                          field.onChange(opt.value);
                          setOpenSelect(false);
                        }}
                      >
                        {opt.label}
                      </div>
                    ))}
                  </div>
                )}

                {error && <p className="text-xs text-[#FCBB64]">{error.message}</p>}
              </div>
            );
          }

          /** FILE INPUT */
          if (isFile) {
            return (
              <div>
                <button
                  type="button"
                  disabled={disabled}
                  onClick={() => fileRef.current?.click()}
                  className={cn(
                    'w-full border border-gray-300 bg-white text-black rounded-md py-2 h-[38px] text-left px-4',
                    'truncate overflow-hidden whitespace-nowrap',
                    inputClassName,
                    disabled && 'cursor-not-allowed opacity-60'
                  )}
                >
                  {field.value
                    ? typeof field.value === 'string'
                      ? field.value.length > 50
                        ? field.value.slice(0, 50) + '...'
                        : field.value
                      : field.value?.name.length > 25
                        ? field.value.name.slice(0, 25) + '...'
                        : field.value.name
                    : 'Upload'}
                </button>

                <input
                  type="file"
                  ref={fileRef}
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0] || '';
                    field.onChange(file);
                    onFileSelect?.(e);
                  }}
                />

                {showPreviewButton && previewNewUrl && (
                  <a
                    href={previewNewUrl}
                    target="_blank"
                    className="text-blue text-sm underline mt-1 block"
                  >
                    Preview File
                  </a>
                )}

                {error && <p className="text-xs text-destructive">{error.message}</p>}
              </div>
            );
          }

          /** TEXTAREA INPUT */
          if (isTextarea) {
            return (
              <>
                <Textarea
                  {...field}
                  id={name}
                  placeholder={placeholder}
                  disabled={disabled}
                  maxLength={maxLength}
                  rows={5}
                  className={cn(
                    'text-black resize-none',
                    error ? 'border-destructive' : '',
                    inputClassName,
                    disabled && 'opacity-80 cursor-not-allowed'
                  )}
                  onChange={(e) => {
                    field.onChange(e);
                    props?.onChange?.(e);
                  }}
                />

                <div className="flex items-center justify-between">
                  {error ? (
                    <p className="text-xs font-semibold text-[#FCBB64]">{error.message}</p>
                  ) : (
                    <span />
                  )}
                  {maxLength && (
                    <span className="text-xs text-muted-foreground">
                      {(field.value?.length ?? 0)}/{maxLength}
                    </span>
                  )}
                </div>
              </>
            );
          }

          return (
            <>
              <div className="relative">
                <Input
                  {...field}
                  id={name}
                  type={isPassword ? (showPassword ? 'text' : 'password') : type}
                  placeholder={placeholder}
                  disabled={disabled}
                  maxLength={maxLength}
                  className={cn(
                    '!h-[38px] text-black',
                    error ? 'border-destructive' : '',
                    inputClassName,
                    disabled && 'opacity-80 cursor-not-allowed'
                  )}
                  onChange={(e) => {
                    field.onChange(e);
                    props?.onChange?.(e);
                  }}
                />

                {isPassword && (
                  <button
                    type="button"
                    className="absolute right-1 top-0 h-full px-2"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <Eye className="text-black" />
                    ) : (
                      <EyeOff className="text-black" />
                    )}
                  </button>
                )}
              </div>

              {error && <p className="text-xs font-semibold text-[#FCBB64]">{error.message}</p>}
            </>
          );
        }}
      />
    </div>
  );
};

export default CustomInput;
